/**
 * Textos do Programa de recomendação. O português fica como padrão no código (chaves `ref_*`
 * em src/i18n/locales); en, es e fr vêm dos arquivos de idioma. Ver homeText.ts.
 */
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { formatNumber, numericDateLocale } from '../../lib/format';
import { localeOf } from '../home/homeText';

export const REFERRAL_PT = {
  eyebrow: 'Programa de recomendação',
  title: 'Indique a {{product}} e ganhe dias de uso',
  lead: 'Quem contratar pelo seu link ganha <b>{{discount}}% de desconto</b>. Você ganha <b>{{percent}}% do tempo contratado</b> em dias de uso.',

  available_label: 'Disponível para resgatar',
  unit_one: 'dia',
  unit_other: 'dias',
  pending_inline: '+{{days}} em {{date}}',
  progress_aria: '{{available}} disponíveis e {{pending}} a receber, de {{target}}',
  stop_1: '1 dia',
  stop_7: '7 dias',
  stop_31: '1 mês',
  stop_92: '3 meses',
  stop_365: '1 ano',
  missing_next: 'Faltam <b>{{missing}}</b> para {{period}}: cerca de {{plans}} pelo seu link.',
  plans_one: '1 plano anual',
  plans_other: '{{n}} planos anuais',
  ready_max: 'Você já pode resgatar <b>1 ano</b>.',
  hint_empty: 'Uma contratação anual pelo seu link rende cerca de {{days}}.',
  hint_lifetime:
    'Sua licença é vitalícia, então não há dias para somar. Seu link continua dando desconto para quem contratar.',
  redeem_button: 'Resgatar dias',
  redeem_locked: 'Libera quando você tiver dias disponíveis.',
  redeem_locked_on: 'Libera em {{date}}.',

  link_label: 'Seu link',
  link_aria: 'Seu link de indicação',
  copy_link: 'Copiar',
  copied: 'Copiado',
  channels: 'Onde compartilhar',
  channel_whatsapp: 'WhatsApp',
  channel_linkedin: 'LinkedIn',
  channel_email: 'Email',
  edit_message: 'Editar mensagem',
  done_editing: 'Pronto',
  message_label: 'Mensagem enviada com o link',
  opened_one: '1 pessoa abriu',
  opened_other: '{{n}} pessoas abriram',
  bought_one: '1 contratou',
  bought_other: '{{n}} contrataram',
  msg_whatsapp:
    'Oi! Tenho usado a {{product}} para achar contatos de clientes e ela tem me ajudado muito na prospecção. Se quiser testar, pelo meu link você ganha {{discount}}% de desconto: {{link}}',
  msg_email_subject: 'Uma ferramenta que uso na prospecção',

  statements_title: 'Extrato',
  statements_which: 'Qual extrato',
  tab_earnings: 'Ganhos',
  tab_redemptions: 'Resgates',
  plan_monthly: 'Plano mensal',
  plan_quarterly: 'Plano trimestral',
  plan_semiannual: 'Plano semestral',
  plan_yearly: 'Plano anual',
  plan_other: 'Plano de {{days}}',
  line_pending: 'A receber · libera em {{date}}',
  line_cancelled: 'Cancelado · compra estornada',
  line_available: 'Disponível',
  earnings_empty:
    'Nenhuma contratação pelo seu link ainda. Quando alguém contratar, ela aparece aqui.',
  redemptions_empty: 'Você ainda não resgatou dias.',
  redemption_license: 'Somados à licença',
  redemption_code: 'Código {{code}}',
  see_full: 'Ver extrato completo',
  see_less: 'Ver menos',
  privacy_statement: 'Não mostramos quem contratou.',

  how_rules_title: 'Como funciona e regulamento',
  how_1_title: 'Você compartilha seu link',
  how_1_text:
    'Com colegas, clientes e grupos de quem vende. A mensagem já vem pronta e você pode editar.',
  how_2_title: 'Quem contratar ganha {{discount}}% de desconto',
  how_2_text: 'O desconto entra sozinho na contratação feita pelo seu link.',
  how_3_title: 'Você ganha {{percent}}% do tempo contratado',
  how_3_text:
    'Em dias de uso, a cada contratação paga pelo seu link. Os dias ficam disponíveis {{hold}} dias depois do pagamento.',
  how_4_title: 'Você soma os dias à sua licença',
  how_4_text:
    'De 1 dia, 7 dias, 1 mês, 3 meses ou 1 ano, direto na sua licença da {{product}}, sem código para ativar.',
  rules_title: 'Regulamento em poucas linhas',
  rules_1: 'Quem contrata pelo seu link ganha {{discount}}% de desconto.',
  rules_2:
    'A cada contratação paga pelo seu link, você ganha {{percent}}% do tempo contratado em dias de uso. Renovações automáticas de assinatura não rendem dias.',
  rules_3:
    'Os dias ficam a receber por {{hold}} dias e, depois, ficam disponíveis. Contratações estornadas não geram dias.',
  rules_4: 'Você troca os dias disponíveis por 1, 7, 31, 92 ou 365 dias somados à sua licença.',
  rules_5: 'Não há pagamento em dinheiro.',
  rules_6: 'Os percentuais podem ser ajustados pela Machine Maker.',
  rules_full: 'Ler o regulamento completo',
  rules_full_title: 'Regulamento do programa',

  error_title: 'Não conseguimos carregar seus números',
  error_text:
    'Pode ser a sua conexão ou uma falha do nosso lado. Seus dias e seus ganhos continuam guardados, e o seu link segue funcionando.',
  retry: 'Tentar de novo',

  redeem_eyebrow: 'Resgate direto na licença',
  redeem_title: 'Somar dias à sua licença',
  redeem_intro:
    'Você tem <b>{{days}}</b> disponíveis. Os dias entram direto na licença, sem código para ativar.',
  period: 'Período',
  per_use: 'de uso',
  fits: 'Cabe nos seus dias',
  suggested: 'Sugerido',
  missing: 'Faltam {{days}}',
  suggestion:
    'Sugerimos {{period}} porque é o maior período que cabe nos seus dias disponíveis. Os outros que cabem também estão liberados.',
  license_ends: 'Licença {{product}} termina em',
  license_ended: 'Licença {{product}} terminou em',
  will_end: 'Vai terminar em',
  confirm: 'Somar {{days}} à licença',
  cancel: 'Cancelar',
  close: 'Fechar',
  after: 'Saem {{days}} dos seus dias disponíveis na hora. Ficam {{left}}.',
  done_eyebrow: 'Dias somados',
  done_title: 'Pronto! Somamos {{days}} à sua licença',
  done_text: 'Sua licença da {{product}} agora termina em <b>{{date}}</b>.',
  redeem_insufficient: 'Seus dias disponíveis mudaram e esse período não cabe mais. Escolha outro.',
  redeem_lifetime: 'Sua licença é vitalícia, então não há dias para somar.',
  redeem_error: 'Não conseguimos somar os dias agora. Tente de novo em instantes.',

  invite_title: 'Ganhe dias de uso indicando a {{product}}',
  invite_discount: 'de desconto para quem você indicar',
  invite_earn: 'do tempo contratado volta para você em dias',
  slug_label: 'Seu link',
  slug_available: 'Disponível',
  slug_checking: 'Conferindo…',
  slug_invalid: 'Use de 3 a 30 letras minúsculas, números ou hífen, sem espaços.',
  slug_taken:
    'Esse identificador já está em uso. Tente outro, com seu sobrenome ou o nome da empresa.',
  slug_hint: 'Não muda depois de ativado.',
  accept: 'Aceito o regulamento',
  read_rules: 'Ler',
  activate: 'Ativar meu link',
  join_error: 'Não conseguimos ativar seu link agora. Tente de novo em instantes.',
  accept_required: 'Marque “Aceito o regulamento” para ativar seu link.',
} as const;

export type ReferralKey = keyof typeof REFERRAL_PT;
export type ReferralVars = Record<string, string | number>;

/** Textos `ref_*` com o português como padrão, mais formatação de números, datas e dias. */
export function useReferralText() {
  const { t, i18n } = useTranslation();
  const lang = (i18n?.language ?? 'pt').slice(0, 2);
  const locale = localeOf(lang);

  const tx = useCallback(
    (key: ReferralKey, vars?: ReferralVars): string =>
      t(`ref_${key}`, {
        defaultValue: REFERRAL_PT[key],
        ...vars,
        interpolation: { escapeValue: false },
      }) as string,
    [t],
  );

  /** "1 dia", "36 dias". */
  const days = useCallback(
    (n: number): string => {
      const one = lang === 'fr' ? n <= 1 : n === 1;
      return `${formatNumber(n, locale)} ${tx(one ? 'unit_one' : 'unit_other')}`;
    },
    [lang, locale, tx],
  );

  const number = useCallback((n: number): string => formatNumber(n, locale), [locale]);

  const date = useCallback(
    (value: string | Date | null | undefined, short = false): string => {
      if (!value) return '';
      const d = new Date(value);
      return new Intl.DateTimeFormat(
        numericDateLocale(locale),
        short
          ? { day: '2-digit', month: '2-digit' }
          : { day: '2-digit', month: '2-digit', year: 'numeric' },
      ).format(d);
    },
    [locale],
  );

  /** Nome de um período de resgate ("1 mês"). */
  const period = useCallback((stop: number): string => tx(`stop_${stop}` as ReferralKey), [tx]);

  return { tx, days, number, date, period, lang };
}
